import { PdCaseStep, PdStepStatus } from './queries';

/** Every timestamp on this board is epoch SECONDS, as elsewhere in the tool. */
export const fmtEpoch = (seconds: number | null | undefined): string => {
  if (!seconds) return 'N/A';
  const d = new Date(seconds * 1000);
  if (Number.isNaN(d.getTime())) return 'N/A';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * "14 min ago" / "6 h ago" / "2 d ago".
 *
 * Relative rather than absolute because the only question this column answers
 * is "has this been sitting too long?", and a wall-clock time makes the reader
 * work that out themselves. A revival's steps are quoted in hours (OTC ~1,
 * IMSI ~24, BPC ~2), so hours and days are the units that matter.
 */
export const fmtAgoEpoch = (seconds: number | null | undefined): string => {
  if (!seconds) return 'N/A';
  const mins = Math.floor((Date.now() - seconds * 1000) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)} h ago`;
  return `${Math.floor(mins / (60 * 24))} d ago`;
};

/**
 * Step colours.
 *
 * BLOCKED is amber rather than red on purpose: it means a precondition is
 * missing (no KYC block to build), which a person can fix — unlike FAILED,
 * where Sensorise refused and someone has to go and find out why.
 */
export const stepChipColor = (status: PdStepStatus): string => {
  switch (status) {
    case 'DONE': return '#2e7d32';
    case 'SENT': return '#0288d1';
    case 'FAILED': return '#d32f2f';
    case 'BLOCKED': return '#ed6c02';
    case 'SKIPPED': return '#9e9e9e';
    default: return '#bdbdbd'; // PENDING
  }
};

export const isRsuReadyAt = (
  rsuReadyAt: number | null | undefined,
  nowSeconds: number = Math.floor(Date.now() / 1000),
): boolean => {
  if (rsuReadyAt == null) return false;
  return nowSeconds >= rsuReadyAt;
};

export const formatRsuCountdown = (
  rsuReadyAt: number | null | undefined,
  nowSeconds: number = Math.floor(Date.now() / 1000),
): string => {
  if (rsuReadyAt == null) return 'timer pending';
  const remaining = Math.max(0, rsuReadyAt - nowSeconds);
  if (remaining <= 0) return 'Ready';
  const hours = Math.floor(remaining / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  const seconds = remaining % 60;
  return `${hours}h ${minutes}m ${seconds}s`;
};

export const getRsuStatusMessage = (
  status: string | undefined,
  rsuReadyAt: number | null | undefined,
  nowSeconds: number = Math.floor(Date.now() / 1000),
): string => {
  if (status !== 'AWAITING_RSU') return '';
  if (isRsuReadyAt(rsuReadyAt, nowSeconds)) return 'RSU trigger is ready';
  return `RSU will unlock in ${formatRsuCountdown(rsuReadyAt, nowSeconds)}`;
};

/** How far along a case is. SKIPPED counts as landed — it is not outstanding work. */
export const progressOf = (
  steps: PdCaseStep[],
): { done: number; total: number } => ({
  done: steps.filter((s) => ['DONE', 'SKIPPED'].includes(s.status)).length,
  total: steps.length,
});
